import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Segmented, Flex, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import SnapshotService from '../services';
import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiPackageDuotone } from 'react-icons/pi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);

const ProductSnapshotChart = ({
  limit = 10,
  period = 'current_month',
  productType,
  lowStock,
  startDate,
  endDate,
  hidePeriodSelector = false
}) => {
  const [currency_symbol, setCurrencySymbol] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPeriod, setCurrentPeriod] = useState(period);

  const getData = useCallback(() => {
    setIsLoading(true);

    let params = {};

    if (hidePeriodSelector && startDate && endDate) {
      params = {
        limit,
        start_date: startDate,
        end_date: endDate,
        sort_order: 'desc'
      };
    } else {
      params = {
        limit: limit,
        period: currentPeriod,
        sort_by:
          currentPeriod === 'current_month'
            ? 'current_month_revenue'
            : currentPeriod === 'last_30_days'
              ? 'last_30_days_revenue'
              : currentPeriod === 'last_90_days'
                ? 'last_90_days_revenue'
                : currentPeriod === 'current_year'
                  ? 'current_year_revenue'
                  : 'total_revenue',
        sort_order: 'desc'
      };
    }

    if (productType) params.item_type = productType;
    if (lowStock) params.low_stock = 'true';

    SnapshotService.getItemSnapshots(params).then((response) => {
      try {
        if (response.data?.success && response.data?.data) {
          const { data: chartData, currency } = response.data.data;
          setData(chartData);
          setCurrencySymbol(currency?.currency_symbol);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching item snapshot data:', error);
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching item snapshot data:', error);
      setIsLoading(false);
    });
  }, [limit, currentPeriod, productType, lowStock, hidePeriodSelector, startDate, endDate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const changePeriod = useCallback((value) => {
    if (hidePeriodSelector) return;
    setCurrentPeriod(value);
  }, [hidePeriodSelector]);

  const getRevenueForPeriod = useCallback(
    (item) => {
      if (hidePeriodSelector && startDate && endDate) {
        return parseFloat(item.period_revenue) || 0;
      }
      switch (currentPeriod) {
        case 'current_month':
          return parseFloat(item.current_month_revenue) || 0;
        case 'last_30_days':
          return parseFloat(item.last_30_days_revenue) || 0;
        case 'last_90_days':
          return parseFloat(item.last_90_days_revenue) || 0;
        case 'current_year':
          return parseFloat(item.current_year_revenue) || 0;
        default:
          return parseFloat(item.total_revenue) || 0;
      }
    },
    [currentPeriod, hidePeriodSelector, startDate, endDate]
  );

  const chartData = useMemo(() => {
    return (data || []).filter(item => getRevenueForPeriod(item) > 0);
  }, [data, getRevenueForPeriod]);

  const productNames = useMemo(
    () => chartData.map(item => item.item_name || item.product_name || 'N/A'),
    [chartData]
  );

  const getRevenueData = useMemo(
    () => chartData.map(item => getRevenueForPeriod(item)),
    [chartData, getRevenueForPeriod]
  );

  const yAxisMax = useMemo(() => {
    if (!getRevenueData.length) return undefined;
    const max = Math.max(...getRevenueData);
    return max > 0 ? max * 1.15 : undefined;
  }, [getRevenueData]);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const productName = firstParam.name || '';
    const index = firstParam.dataIndex;
    const item = chartData[index];
    
    if (!item) return '';

    const revenue = getRevenueForPeriod(item);

    const salesCount =
      hidePeriodSelector && startDate && endDate
        ? undefined
        : currentPeriod === 'current_month'
          ? item.current_month_sales_count
          : currentPeriod === 'last_30_days'
            ? item.last_30_days_sales_count
            : currentPeriod === 'last_90_days'
              ? item.last_90_days_sales_count
              : currentPeriod === 'current_year'
                ? item.current_year_sales_count
                : item.total_sales_count;

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${productName}</div>
        <div style="margin: 4px 0;">
          Revenue: ${formatCurrency(revenue, currency_symbol)}
        </div>
        ${salesCount != null ? `<div style="margin: 4px 0;">Sales Count: ${salesCount || 0}</div>` : ''}
        ${item.track_inventory ? `<div style="margin: 4px 0;">Stock: ${item.current_stock || 0}</div>` : ''}
      </div>
    `;
  }, [currency_symbol, chartData, getRevenueForPeriod, currentPeriod, hidePeriodSelector, startDate, endDate]);

  const getOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: { color: '#fff' }
    },
    xAxis: {
      type: 'category',
      data: productNames,
      axisPointer: { type: 'shadow' },
      axisLabel: {
        color: getColor('gray-400'),
        rotate: productNames.length > 5 ? 45 : 0,
        interval: 0
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: yAxisMax,
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        formatter: (value) => {
          return `${currency_symbol} ${numberFormatter(value, 0)}`
        },
        margin: 15
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        data: getRevenueData,
        itemStyle: {
          color: getColor('info')
        },
        barMaxWidth: 50,
        barMinHeight: 6
      }
    ],
    legend: {
      data: ['Revenue'],
      bottom: 0,
      textStyle: {
        color: getColor('gray-400')
      }
    },
    grid: { right: 10, left: 0, bottom: 30, top: 10, containLabel: true }
  }), [productNames, currency_symbol, tooltipFormatter, getRevenueData, yAxisMax]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const hasData = chartData.length > 0 && getRevenueData.some(value => value > 0);

  if (!hasData) {
    return (
      <CardEmptyResult
        title="No product performance data"
        subtitle="There is no product revenue data available for the selected period."
        icon={<PiPackageDuotone className="fs-2 text-secondary" />}
      />
    );
  }

  return (
    <>
      <Flex justify="space-between" align="center">
        <h6>Product Performance</h6>
        {!hidePeriodSelector && (
          <Segmented
            options={[
              { label: 'This Month', value: 'current_month' },
              { label: 'Last 30 Days', value: 'last_30_days' },
              { label: 'Last 90 Days', value: 'last_90_days' },
              { label: 'This Year', value: 'current_year' }
            ]}
            value={currentPeriod}
            onChange={changePeriod}
          />
        )}
      </Flex>
      <ReactEChartsCore
        echarts={echarts}
        option={getOptions}
        style={{ minHeight: '300px' }}
      />
    </>
  );
};

export default ProductSnapshotChart;

